'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdEdit,
    MdInventory,
    MdLocalHospital,
    MdSend,
    MdCheckCircle,
    MdCancel,
    MdDownload,
    MdAdd,
    MdDelete,
    MdVisibility,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getBillingBatch as getBatch,
    submitBatch,
    acknowledgeBatch,
    processBatchResponse,
    closeBatch,
    addGuidesToBatch as addGuideToBatch,
    removeGuideFromBatch,
    BillingBatch,
    BATCH_STATUS_LABELS,
    BATCH_STATUS_COLORS,
} from '@/lib/api/billing/billing-batch-api'
import { GUIDE_STATUS_LABELS, GUIDE_STATUS_COLORS } from '@/lib/api/billing/guide-api'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import ConfirmModal from '@/components/confirm-modal'

export default function BatchDetailPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('billing', 'batches')

    const [batch, setBatch] = useState<BillingBatch | null>(null)
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [showResponseModal, setShowResponseModal] = useState(false)
    const [showRemoveGuideModal, setShowRemoveGuideModal] = useState<
        number | null
    >(null)
    const [showCloseModal, setShowCloseModal] = useState(false)

    const [responseData, setResponseData] = useState({
        protocolNumber: '',
        totalPaid: '',
        totalGlossed: '',
        paymentDate: '',
    })

    useEffect(() => {
        if (id && user?.companyId) {
            loadBatch()
        }
    }, [id, user?.companyId])

    const loadBatch = async () => {
        if (!id) return

        setLoading(true)
        try {
            const response = await getBatch(parseInt(id, 10))
            if (response.success && response.result) {
                setBatch(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar lote')
                navigate('/billing/batches')
            }
        } catch (error) {
            toast.error('Erro ao carregar lote')
            navigate('/billing/batches')
        } finally {
            setLoading(false)
        }
    }

    const handleSubmit = async () => {
        if (!id) return

        setActionLoading(true)
        try {
            const response = await submitBatch(parseInt(id, 10))
            if (response.success) {
                toast.success('Lote enviado para o convênio')
                loadBatch()
            } else {
                toast.error(response.error || 'Erro ao enviar lote')
            }
        } catch (error) {
            toast.error('Erro ao enviar lote')
        } finally {
            setActionLoading(false)
        }
    }

    const handleAcknowledge = async () => {
        if (!id) return

        setActionLoading(true)
        try {
            const response = await acknowledgeBatch(parseInt(id, 10))
            if (response.success) {
                toast.success('Lote confirmado como recebido')
                loadBatch()
            } else {
                toast.error(response.error || 'Erro ao confirmar lote')
            }
        } catch (error) {
            toast.error('Erro ao confirmar lote')
        } finally {
            setActionLoading(false)
        }
    }

    const handleProcessResponse = async () => {
        if (!id) return

        setActionLoading(true)
        try {
            const response = await processBatchResponse(parseInt(id, 10), {
                protocolNumber: responseData.protocolNumber || undefined,
                totalPaid: responseData.totalPaid
                    ? parseFloat(responseData.totalPaid)
                    : undefined,
                totalGlossed: responseData.totalGlossed
                    ? parseFloat(responseData.totalGlossed)
                    : undefined,
                paymentDate: responseData.paymentDate || undefined,
            })
            if (response.success) {
                toast.success('Resposta do convênio processada')
                setShowResponseModal(false)
                setResponseData({
                    protocolNumber: '',
                    totalPaid: '',
                    totalGlossed: '',
                    paymentDate: '',
                })
                loadBatch()
            } else {
                toast.error(response.error || 'Erro ao processar resposta')
            }
        } catch (error) {
            toast.error('Erro ao processar resposta')
        } finally {
            setActionLoading(false)
        }
    }

    const handleClose = () => {
        setShowCloseModal(true)
    }

    const confirmClose = async () => {
        if (!id) return

        setActionLoading(true)
        try {
            const response = await closeBatch(parseInt(id, 10))
            if (response.success) {
                toast.success('Lote fechado com sucesso')
                loadBatch()
            } else {
                toast.error(response.error || 'Erro ao fechar lote')
            }
        } catch (error) {
            toast.error('Erro ao fechar lote')
        } finally {
            setActionLoading(false)
            setShowCloseModal(false)
        }
    }

    const handleRemoveGuide = async (guideId: number) => {
        if (!id) return

        setActionLoading(true)
        try {
            const response = await removeGuideFromBatch(
                parseInt(id, 10),
                guideId
            )
            if (response.success) {
                toast.success('Guia removida do lote')
                setShowRemoveGuideModal(null)
                loadBatch()
            } else {
                toast.error(response.error || 'Erro ao remover guia')
            }
        } catch (error) {
            toast.error('Erro ao remover guia')
        } finally {
            setActionLoading(false)
        }
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value)
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando lote...
                </p>
            </div>
        )
    }

    if (!batch) {
        return null
    }

    const canSubmitBatch = batch.status === 'BATCH_OPEN' || batch.status === 'BATCH_DRAFT'
    const canAcknowledge = batch.status === 'BATCH_SUBMITTED'
    const canProcessResponse = batch.status === 'BATCH_RECEIVED' || batch.status === 'BATCH_PROCESSED'
    const canCloseBatch = ['BATCH_RECEIVED', 'BATCH_PARTIALLY_PAID', 'BATCH_PROCESSED'].includes(
        batch.status
    )
    const canEditBatch = batch.status === 'BATCH_OPEN' || batch.status === 'BATCH_DRAFT'

    return (
        <div className="flex flex-col gap-6 p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/billing/batches')}
                        className="btn btn-ghost btn-sm"
                    >
                        <MdArrowBack className="text-lg" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                        <MdInventory className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {batch.name || `Lote #${batch.id}`}
                        </h1>
                        <div className="flex items-center gap-2 mt-1">
                            <span
                                className={`badge ${BATCH_STATUS_COLORS[batch.status]}`}
                            >
                                {BATCH_STATUS_LABELS[batch.status]}
                            </span>
                            <span className="badge badge-ghost badge-sm">
                                {batch.referenceMonth || `${batch.competenceMonth}/${batch.competenceYear}`}
                            </span>
                        </div>
                    </div>
                </div>
                {canUpdate && (
                    <div className="flex items-center gap-2 flex-wrap">
                        {canSubmitBatch && (
                            <button
                                onClick={handleSubmit}
                                className="btn btn-info btn-sm gap-2"
                                disabled={actionLoading}
                            >
                                <MdSend className="text-lg" />
                                Enviar
                            </button>
                        )}
                        {canAcknowledge && (
                            <button
                                onClick={handleAcknowledge}
                                className="btn btn-success btn-sm gap-2"
                                disabled={actionLoading}
                            >
                                <MdCheckCircle className="text-lg" />
                                Confirmar Recebimento
                            </button>
                        )}
                        {canProcessResponse && (
                            <button
                                onClick={() => setShowResponseModal(true)}
                                className="btn btn-secondary btn-sm gap-2"
                                disabled={actionLoading}
                            >
                                <MdDownload className="text-lg" />
                                Registrar Resposta
                            </button>
                        )}
                        {canCloseBatch && (
                            <button
                                onClick={handleClose}
                                className="btn btn-warning btn-sm gap-2"
                                disabled={actionLoading}
                            >
                                <MdCancel className="text-lg" />
                                Fechar Lote
                            </button>
                        )}
                        {canEditBatch && (
                            <button
                                onClick={() =>
                                    navigate(`/billing/batches/${id}/edit`)
                                }
                                className="btn btn-secondary btn-sm gap-2"
                            >
                                <MdEdit className="text-lg" />
                                Editar
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Value Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <MdLocalHospital className="text-secondary" />
                            <span className="text-xs text-base-content/60 uppercase">
                                Convênio
                            </span>
                        </div>
                        <p className="font-semibold">
                            {batch.healthPlan?.name || '-'}
                        </p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase">
                            Total Faturado
                        </span>
                        <p className="text-xl font-bold text-secondary">
                            {formatCurrency(batch.totalValue)}
                        </p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase">
                            Total Pago
                        </span>
                        <p
                            className={`text-xl font-bold ${batch.totalPaid ? 'text-success' : ''}`}
                        >
                            {formatCurrency(batch.totalPaid || 0)}
                        </p>
                    </div>
                </div>
                <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                    <div className="card-body p-4">
                        <span className="text-xs text-base-content/60 uppercase">
                            Total Glosado
                        </span>
                        <p
                            className={`text-xl font-bold ${batch.totalGlossed ? 'text-error' : ''}`}
                        >
                            {formatCurrency(batch.totalGloss || 0)}
                        </p>
                    </div>
                </div>
            </div>

            {/* Details */}
            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-4">Detalhes</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                            <p className="text-xs text-base-content/60 uppercase">
                                Data de Vencimento
                            </p>
                            <p className="font-semibold">
                                {batch.dueDate
                                    ? format(
                                          new Date(batch.dueDate),
                                          'dd/MM/yyyy',
                                          { locale: ptBR }
                                      )
                                    : '-'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-base-content/60 uppercase">
                                Data de Envio
                            </p>
                            <p className="font-semibold">
                                {batch.submittedAt
                                    ? format(
                                          new Date(batch.submittedAt),
                                          'dd/MM/yyyy',
                                          { locale: ptBR }
                                      )
                                    : '-'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-base-content/60 uppercase">
                                Protocolo
                            </p>
                            <p className="font-semibold font-mono">
                                {batch.submissionProtocol || '-'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-base-content/60 uppercase">
                                Data Pagamento
                            </p>
                            <p className="font-semibold">
                                {batch.processedAt
                                    ? format(
                                          new Date(batch.processedAt),
                                          'dd/MM/yyyy',
                                          { locale: ptBR }
                                      )
                                    : '-'}
                            </p>
                        </div>
                    </div>
                    {batch.notes && (
                        <div className="mt-4">
                            <p className="text-xs text-base-content/60 uppercase mb-1">
                                Observações
                            </p>
                            <p className="p-3 bg-base-200/50 rounded-lg">
                                {batch.notes}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Guides */}
            <div className="card bg-base-100 border border-base-300/50 shadow-sm">
                <div className="card-body">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="card-title text-lg">
                            Guias do Lote ({batch.guides?.length || 0})
                        </h2>
                        {canCreate && canEditBatch && (
                            <button
                                onClick={() =>
                                    navigate(`/billing/batches/${id}/guias/add`)
                                }
                                className="btn btn-secondary btn-sm gap-2"
                            >
                                <MdAdd className="text-lg" />
                                Adicionar Guia
                            </button>
                        )}
                    </div>

                    {!batch.guides || batch.guides.length === 0 ? (
                        <div className="text-center py-8 text-base-content/60">
                            <MdInventory className="text-4xl mx-auto mb-2 opacity-40" />
                            <p>Nenhuma guia neste lote</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Guia</th>
                                        <th>Paciente</th>
                                        <th>Procedimento</th>
                                        <th>Status</th>
                                        <th className="text-right">Valor</th>
                                        {(canUpdate || canDelete) && <th></th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {batch.guides.map(guide => (
                                        <tr key={guide.id} className="hover">
                                            <td className="font-mono text-sm">
                                                {guide.guideNumber}
                                            </td>
                                            <td>
                                                {guide.client?.name || '-'}
                                            </td>
                                            <td className="text-sm">
                                                {guide.mainOrderName || '-'}
                                            </td>
                                            <td>
                                                <span
                                                    className={`badge badge-sm ${GUIDE_STATUS_COLORS[guide.status]}`}
                                                >
                                                    {
                                                        GUIDE_STATUS_LABELS[
                                                            guide.status
                                                        ]
                                                    }
                                                </span>
                                            </td>
                                            <td className="text-right font-semibold">
                                                {formatCurrency(
                                                    guide.totalAuthorized ||
                                                        guide.totalRequested
                                                )}
                                            </td>
                                            {(canUpdate || canDelete) && (
                                                <td>
                                                    <div className="flex items-center gap-1 justify-end">
                                                        <button
                                                            onClick={() =>
                                                                navigate(
                                                                    `/billing/guides/${guide.id}`
                                                                )
                                                            }
                                                            className="btn btn-ghost btn-xs"
                                                        >
                                                            <MdVisibility className="text-lg" />
                                                        </button>
                                                        {canDelete && canEditBatch && (
                                                            <button
                                                                onClick={() =>
                                                                    setShowRemoveGuideModal(
                                                                        guide.id
                                                                    )
                                                                }
                                                                className="btn btn-ghost btn-xs text-error"
                                                            >
                                                                <MdDelete className="text-lg" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td
                                            colSpan={(canUpdate || canDelete) ? 4 : 3}
                                            className="text-right font-semibold"
                                        >
                                            Total:
                                        </td>
                                        <td className="text-right font-bold text-secondary">
                                            {formatCurrency(batch.totalValue)}
                                        </td>
                                        {(canUpdate || canDelete) && <td></td>}
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Response Modal */}
            {showResponseModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">
                            Registrar Resposta do Convênio
                        </h3>
                        <div className="py-4 space-y-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">
                                        Número do Protocolo
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    value={responseData.protocolNumber}
                                    onChange={e =>
                                        setResponseData(prev => ({
                                            ...prev,
                                            protocolNumber: e.target.value,
                                        }))
                                    }
                                    placeholder="Protocolo de recebimento"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">
                                            Valor Pago (R$)
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className="input input-bordered w-full"
                                        value={responseData.totalPaid}
                                        onChange={e =>
                                            setResponseData(prev => ({
                                                ...prev,
                                                totalPaid: e.target.value,
                                            }))
                                        }
                                        placeholder="0,00"
                                    />
                                </div>
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">
                                            Valor Glosado (R$)
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        className="input input-bordered w-full"
                                        value={responseData.totalGlossed}
                                        onChange={e =>
                                            setResponseData(prev => ({
                                                ...prev,
                                                totalGlossed: e.target.value,
                                            }))
                                        }
                                        placeholder="0,00"
                                    />
                                </div>
                            </div>
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">
                                        Data do Pagamento
                                    </span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={responseData.paymentDate}
                                    onChange={e =>
                                        setResponseData(prev => ({
                                            ...prev,
                                            paymentDate: e.target.value,
                                        }))
                                    }
                                />
                            </div>
                        </div>
                        <div className="modal-action">
                            <button
                                onClick={() => {
                                    setShowResponseModal(false)
                                    setResponseData({
                                        protocolNumber: '',
                                        totalPaid: '',
                                        totalGlossed: '',
                                        paymentDate: '',
                                    })
                                }}
                                className="btn btn-ghost"
                                disabled={actionLoading}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleProcessResponse}
                                className="btn btn-secondary"
                                disabled={actionLoading}
                            >
                                {actionLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Registrar'
                                )}
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setShowResponseModal(false)}
                    ></div>
                </div>
            )}

            {/* Remove Guide Modal */}
            {showRemoveGuideModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg">Remover Guia</h3>
                        <p className="py-4">
                            Tem certeza que deseja remover esta guia do lote?
                        </p>
                        <div className="modal-action">
                            <button
                                onClick={() => setShowRemoveGuideModal(null)}
                                className="btn btn-ghost"
                                disabled={actionLoading}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() =>
                                    handleRemoveGuide(showRemoveGuideModal)
                                }
                                className="btn btn-error"
                                disabled={actionLoading}
                            >
                                {actionLoading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Remover'
                                )}
                            </button>
                        </div>
                    </div>
                    <div
                        className="modal-backdrop"
                        onClick={() => setShowRemoveGuideModal(null)}
                    ></div>
                </div>
            )}

            <ConfirmModal
                isOpen={showCloseModal}
                onClose={() => setShowCloseModal(false)}
                onConfirm={confirmClose}
                title="Fechar lote"
                message="Fechar este lote de faturamento?"
                confirmButtonText="Fechar"
                cancelButtonText="Cancelar"
                loading={actionLoading}
            />
        </div>
    )
}
